import ChatbotService from './chatbot.service'

class SimpleTaskTracker {
  constructor() {
    this.activeTasks = new Map()
    this.isOnline = navigator.onLine
    
    window.addEventListener('online', () => {
      this.isOnline = true
      console.log('Network connection restored')
      this.handleNetworkRestore()
    })
    
    window.addEventListener('offline', () => {
      this.isOnline = false
      console.log('Network connection lost')
    })
  }

  async trackTask(taskId, callbacks = {}) {
    console.log(`Tracking task ${taskId} with polling`)
    
    const task = {
      id: taskId,
      active: true,
      callbacks: {
        onProgress: callbacks.onProgress || (() => {}),
        onSuccess: callbacks.onSuccess || (() => {}),
        onError: callbacks.onError || (() => {})
      }
    }
    
    this.activeTasks.set(taskId, task)
    
    this.startPolling(task)
    
    return {
      stop: () => this.stopTask(taskId)
    }
  }

  async startPolling(task) {
    let retryCount = 0
    const maxRetries = 5
    const baseDelay = 1000
    const maxDelay = 30000 
    
    const poll = async () => {
      if (!task.active) return
      
      try {
        const response = await ChatbotService.getTaskStatus(task.id)

        if (!response) {
          setTimeout(poll, 5000)
          return
        }

        retryCount = 0

        if (response.status === 'completed') {
          task.callbacks.onSuccess(response)
          this.stopTask(task.id)
        } else if (response.status === 'failed') {
          task.callbacks.onError(new Error(response.message || 'Task failed'))
          this.stopTask(task.id)
        } else {
          task.callbacks.onProgress(response)
          setTimeout(poll, 5000) 
        }
      } catch (error) {
        console.error('Error polling task', error)
        
        // Check if it's a network error that might be temporary
        const isNetworkError = error.status === 0 || 
                              error.message?.includes('Network Error') ||
                              error.message?.includes('ERR_NETWORK_CHANGED') ||
                              error.message?.includes('Could not connect to the server')
        
        if (isNetworkError && retryCount < maxRetries) {
          retryCount++
          
          const exponentialDelay = Math.min(baseDelay * Math.pow(2, retryCount - 1), maxDelay)
          const jitter = Math.random() * 1000 
          const retryDelay = Math.floor(exponentialDelay + jitter)
          
          console.log(`Retrying polling (attempt ${retryCount}/${maxRetries}) in ${retryDelay}ms`)
          setTimeout(poll, retryDelay)
          return
        }
        
        task.callbacks.onError(error)
        this.stopTask(task.id)
      }
    }
    
    poll()
  }


  stopTask(taskId) {
    const task = this.activeTasks.get(taskId)
    if (task) {
      task.active = false
      this.activeTasks.delete(taskId)
    }
  }

  handleNetworkRestore() {
    if (this.activeTasks.size > 0) {
      this.activeTasks.forEach((task) => {
        if (task.active) {
          this.startPolling(task)
        }
      })
    }
  }

  disconnect() {
    this.activeTasks.forEach((task) => {
      task.active = false
    })
    this.activeTasks.clear()
  }
}

const taskTracker = new SimpleTaskTracker()

export default taskTracker
